import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { toast } from 'react-hot-toast';

import { englishService } from '../services/englishService';
import { EnglishLiveSessionCompletion, EnglishLiveSessionGrant } from '../types/english';

type LiveStatus = 'idle' | 'connecting' | 'connected' | 'ended' | 'error';

interface UseEnglishLiveSessionOptions {
  grant: EnglishLiveSessionGrant | null;
  onCompleted?: (completion: EnglishLiveSessionCompletion) => Promise<void> | void;
}

const PCM16_INPUT_MIME = 'audio/pcm;rate=16000';
const PCM16_OUTPUT_SAMPLE_RATE = 24000;

const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let index = 0; index < bytes.length; index += 1) {
    binary += String.fromCharCode(bytes[index]);
  }
  return window.btoa(binary);
};

const base64ToUint8Array = (base64: string): Uint8Array => {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
};

const downsampleTo16k = (input: Float32Array, sampleRate: number): Float32Array => {
  if (sampleRate === 16000) {
    return input;
  }
  const ratio = sampleRate / 16000;
  const newLength = Math.max(1, Math.round(input.length / ratio));
  const result = new Float32Array(newLength);
  let outputIndex = 0;
  let inputIndex = 0;
  while (outputIndex < result.length) {
    const nextIndex = Math.min(input.length, Math.round((outputIndex + 1) * ratio));
    let sum = 0;
    let count = 0;
    while (inputIndex < nextIndex) {
      sum += input[inputIndex];
      count += 1;
      inputIndex += 1;
    }
    result[outputIndex] = count > 0 ? sum / count : 0;
    outputIndex += 1;
  }
  return result;
};

const float32ToPcm16 = (input: Float32Array): Int16Array => {
  const buffer = new Int16Array(input.length);
  for (let index = 0; index < input.length; index += 1) {
    const sample = Math.max(-1, Math.min(1, input[index]));
    buffer[index] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
  }
  return buffer;
};

const pcm16ToFloat32 = (bytes: Uint8Array): Float32Array => {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const samples = new Float32Array(bytes.byteLength / 2);
  for (let index = 0; index < samples.length; index += 1) {
    samples[index] = view.getInt16(index * 2, true) / 0x8000;
  }
  return samples;
};

export const useEnglishLiveSession = ({
  grant,
  onCompleted,
}: UseEnglishLiveSessionOptions) => {
  const [status, setStatus] = useState<LiveStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [isMicActive, setIsMicActive] = useState(false);
  const [inputTranscript, setInputTranscript] = useState('');
  const [outputTranscript, setOutputTranscript] = useState('');
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [lastCompletion, setLastCompletion] = useState<EnglishLiveSessionCompletion | null>(null);

  const websocketRef = useRef<WebSocket | null>(null);
  const outputAudioContextRef = useRef<AudioContext | null>(null);
  const playbackCursorRef = useRef(0);
  const activeSourcesRef = useRef<AudioBufferSourceNode[]>([]);
  const inputAudioContextRef = useRef<AudioContext | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const processorNodeRef = useRef<ScriptProcessorNode | null>(null);
  const monitorGainRef = useRef<GainNode | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const sessionStartedAtRef = useRef<number | null>(null);
  const elapsedIntervalRef = useRef<number | null>(null);
  const maxDurationTimerRef = useRef<number | null>(null);
  const completionInFlightRef = useRef(false);
  const inputPartsRef = useRef<string[]>([]);
  const outputPartsRef = useRef<string[]>([]);
  const usedGrantIdRef = useRef<string | null>(null);

  const stopPlayback = useCallback(() => {
    activeSourcesRef.current.forEach((source) => {
      try {
        source.stop();
      } catch {
        // ignore
      }
    });
    activeSourcesRef.current = [];
    if (outputAudioContextRef.current) {
      playbackCursorRef.current = outputAudioContextRef.current.currentTime;
    } else {
      playbackCursorRef.current = 0;
    }
  }, []);

  const clearTimers = useCallback(() => {
    if (elapsedIntervalRef.current) {
      window.clearInterval(elapsedIntervalRef.current);
      elapsedIntervalRef.current = null;
    }
    if (maxDurationTimerRef.current) {
      window.clearTimeout(maxDurationTimerRef.current);
      maxDurationTimerRef.current = null;
    }
  }, []);

  const stopMicrophone = useCallback(() => {
    if (processorNodeRef.current) {
      processorNodeRef.current.disconnect();
      processorNodeRef.current.onaudioprocess = null;
      processorNodeRef.current = null;
    }
    if (sourceNodeRef.current) {
      sourceNodeRef.current.disconnect();
      sourceNodeRef.current = null;
    }
    if (monitorGainRef.current) {
      monitorGainRef.current.disconnect();
      monitorGainRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
    if (inputAudioContextRef.current) {
      void inputAudioContextRef.current.close();
      inputAudioContextRef.current = null;
    }
    if (websocketRef.current?.readyState === WebSocket.OPEN) {
      websocketRef.current.send(JSON.stringify({ realtimeInput: { audioStreamEnd: true } }));
    }
    setIsMicActive(false);
  }, []);

  const finishSession = useCallback(async (reason: string) => {
    if (!grant || completionInFlightRef.current || !sessionStartedAtRef.current) {
      return;
    }
    completionInFlightRef.current = true;
    try {
      const actualDurationSeconds = Math.max(
        1,
        Math.round((Date.now() - sessionStartedAtRef.current) / 1000),
      );
      const transcript = [
        inputPartsRef.current.length ? `Learner: ${inputPartsRef.current.join(' ')}` : '',
        outputPartsRef.current.length ? `Coach: ${outputPartsRef.current.join(' ')}` : '',
      ]
        .filter(Boolean)
        .join('\n');
      const completion = await englishService.completeLiveSession(
        grant.session_id,
        transcript,
        actualDurationSeconds,
        {
          reason,
          input_transcript_parts: inputPartsRef.current,
          output_transcript_parts: outputPartsRef.current,
          reference_text: grant.reference_text || null,
          coach_goal: grant.coach_goal || null,
        },
        {
          latest_coach_feedback: outputPartsRef.current[outputPartsRef.current.length - 1] || '',
          mode: grant.mode,
        },
      );
      setLastCompletion(completion);
      await onCompleted?.(completion);
    } catch (completionError: any) {
      console.error('Failed to complete English Live session:', completionError);
      toast.error(completionError?.response?.data?.detail || 'Could not save the English Live session result.');
    } finally {
      completionInFlightRef.current = false;
    }
  }, [grant, onCompleted]);

  const cleanup = useCallback(async (reason: string, shouldPersist: boolean) => {
    stopMicrophone();
    clearTimers();
    stopPlayback();
    if (websocketRef.current) {
      try {
        websocketRef.current.close();
      } catch {
        // ignore
      }
      websocketRef.current = null;
    }
    if (outputAudioContextRef.current) {
      void outputAudioContextRef.current.close();
      outputAudioContextRef.current = null;
    }
    if (shouldPersist) {
      await finishSession(reason);
    }
  }, [clearTimers, finishSession, stopMicrophone, stopPlayback]);

  const appendTranscript = useCallback((
    nextText: string,
    kind: 'input' | 'output',
  ) => {
    const trimmed = nextText.trim();
    if (!trimmed) return;
    const target = kind === 'input' ? inputPartsRef.current : outputPartsRef.current;
    if (target[target.length - 1] !== trimmed) {
      target.push(trimmed);
    }
    if (kind === 'input') {
      setInputTranscript(target.join(' '));
    } else {
      setOutputTranscript(target.join(' '));
    }
  }, []);

  const playAudioChunk = useCallback(async (base64Data: string) => {
    if (!base64Data) return;
    if (!outputAudioContextRef.current) {
      outputAudioContextRef.current = new AudioContext({ sampleRate: PCM16_OUTPUT_SAMPLE_RATE });
      playbackCursorRef.current = outputAudioContextRef.current.currentTime;
    }
    const context = outputAudioContextRef.current;
    if (context.state === 'suspended') {
      await context.resume();
    }
    const bytes = base64ToUint8Array(base64Data);
    const samples = pcm16ToFloat32(bytes);
    const audioBuffer = context.createBuffer(1, samples.length, PCM16_OUTPUT_SAMPLE_RATE);
    audioBuffer.copyToChannel(samples, 0);
    const source = context.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(context.destination);
    const startAt = Math.max(playbackCursorRef.current, context.currentTime);
    source.start(startAt);
    playbackCursorRef.current = startAt + audioBuffer.duration;
    activeSourcesRef.current.push(source);
    source.onended = () => {
      activeSourcesRef.current = activeSourcesRef.current.filter((entry) => entry !== source);
    };
  }, []);

  const connect = useCallback(async () => {
    if (!grant) {
      toast.error('Reserve a Live session first.');
      return;
    }
    if (status === 'connecting' || status === 'connected') {
      return;
    }
    setError(null);
    setStatus('connecting');
    setInputTranscript('');
    setOutputTranscript('');
    inputPartsRef.current = [];
    outputPartsRef.current = [];
    sessionStartedAtRef.current = Date.now();
    setElapsedSeconds(0);

    const websocket = new WebSocket(grant.websocket_url);
    websocketRef.current = websocket;

    websocket.onopen = () => {
      websocket.send(JSON.stringify(grant.setup_payload));
    };

    websocket.onmessage = (event) => {
      try {
        const response = JSON.parse(event.data);
        if (response.setupComplete) {
          setStatus('connected');
          usedGrantIdRef.current = grant.session_id;
          clearTimers();
          elapsedIntervalRef.current = window.setInterval(() => {
            if (!sessionStartedAtRef.current) return;
            setElapsedSeconds(Math.max(0, Math.round((Date.now() - sessionStartedAtRef.current) / 1000)));
          }, 1000);
          maxDurationTimerRef.current = window.setTimeout(() => {
            void cleanup('max_duration_reached', true);
            setStatus('ended');
            toast('This Live session reached its time limit.');
          }, Math.max(grant.max_duration_seconds, 1) * 1000);
          if (grant.initial_client_content) {
            websocket.send(JSON.stringify(grant.initial_client_content));
          }
          return;
        }
        if (response.serverContent) {
          const serverContent = response.serverContent;
          if (serverContent.interrupted) {
            stopPlayback();
          }
          if (serverContent.inputTranscription?.text) {
            appendTranscript(String(serverContent.inputTranscription.text), 'input');
          }
          if (serverContent.outputTranscription?.text) {
            appendTranscript(String(serverContent.outputTranscription.text), 'output');
          }
          if (serverContent.modelTurn?.parts) {
            for (const part of serverContent.modelTurn.parts) {
              if (part.inlineData?.data) {
                void playAudioChunk(String(part.inlineData.data));
              }
            }
          }
        }
        if (response.goAway) {
          toast('The Live session is about to disconnect. Please finish soon.');
        }
      } catch (parseError) {
        console.error('Could not parse Gemini Live message:', parseError);
      }
    };

    websocket.onerror = () => {
      setError('The Live session could not connect.');
      setStatus('error');
    };

    websocket.onclose = () => {
      setStatus((current) => (current === 'ended' ? current : 'idle'));
    };
  }, [appendTranscript, cleanup, clearTimers, grant, playAudioChunk, status, stopPlayback]);

  const startMicrophone = useCallback(async () => {
    if (!websocketRef.current || websocketRef.current.readyState !== WebSocket.OPEN) {
      toast.error('Connect the Live session before starting the microphone.');
      return;
    }
    if (isMicActive) {
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      const audioContext = new AudioContext();
      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      const monitorGain = audioContext.createGain();
      monitorGain.gain.value = 0;

      processor.onaudioprocess = (event) => {
        const socket = websocketRef.current;
        if (!socket || socket.readyState !== WebSocket.OPEN) {
          return;
        }
        const channel = event.inputBuffer.getChannelData(0);
        const downsampled = downsampleTo16k(channel, audioContext.sampleRate);
        const pcm16 = float32ToPcm16(downsampled);
        socket.send(
          JSON.stringify({
            realtimeInput: {
              audio: {
                data: arrayBufferToBase64(pcm16.buffer),
                mimeType: PCM16_INPUT_MIME,
              },
            },
          }),
        );
      };

      source.connect(processor);
      processor.connect(monitorGain);
      monitorGain.connect(audioContext.destination);

      mediaStreamRef.current = stream;
      inputAudioContextRef.current = audioContext;
      sourceNodeRef.current = source;
      processorNodeRef.current = processor;
      monitorGainRef.current = monitorGain;
      setIsMicActive(true);
    } catch (micError) {
      console.error('Microphone start failed:', micError);
      toast.error('Could not access the microphone for the Live lesson.');
      setError('Microphone access was denied.');
      setStatus('error');
    }
  }, [isMicActive]);

  const disconnect = useCallback(async (reason = 'manual_end') => {
    await cleanup(reason, Boolean(usedGrantIdRef.current));
    usedGrantIdRef.current = null;
    setIsMicActive(false);
    setStatus('ended');
  }, [cleanup]);

  useEffect(() => {
    return () => {
      void cleanup('component_unmount', Boolean(usedGrantIdRef.current));
      usedGrantIdRef.current = null;
    };
  }, [cleanup]);

  useEffect(() => {
    setStatus('idle');
    setError(null);
    setIsMicActive(false);
    setInputTranscript('');
    setOutputTranscript('');
    setElapsedSeconds(0);
    setLastCompletion(null);
    inputPartsRef.current = [];
    outputPartsRef.current = [];
    usedGrantIdRef.current = null;
  }, [grant?.session_id]);

  const isConnected = status === 'connected';
  const canConnect = Boolean(grant) && !isConnected && status !== 'connecting';

  const transcriptSummary = useMemo(() => ({
    input: inputTranscript,
    output: outputTranscript,
  }), [inputTranscript, outputTranscript]);

  return {
    status,
    error,
    isConnected,
    isConnecting: status === 'connecting',
    isMicActive,
    canConnect,
    elapsedSeconds,
    lastCompletion,
    transcriptSummary,
    connect,
    startMicrophone,
    stopMicrophone,
    disconnect,
  };
};
